import React from "react";
import { Link } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
} from "@mui/x-data-grid";
import { useDataGridStyles } from "../../../hooks/useDataGridStyles";

import { format } from "date-fns";
import { IPurchaseOrderWithDetails } from "../../../api/Account/myOrdersApi";
import { myPurchasesResources } from "./.resources";
import { useResources } from "../../../hooks/useResources";
import { IUserV0 } from "../../../api/Account/usersApi";
import { ILoggedUser } from "../../../types/Auth";
import * as urls from "../../../types/Constants";

//https://devexpress.github.io/devextreme-reactive/react/grid/demos/featured/tree-data/

export interface IPurchaseOrderRow extends IPurchaseOrderWithDetails {
  id: string;
  ownerName?: string;
  ownerCompany?: string;
  endUserName?: string;
  endUserCompany?: string;
  singleProductOrder: string;
  index: number;
  hasChildren: boolean;
  parentId?: string;
}

export interface IMyPurchasesGridProps {
  orders: IPurchaseOrderRow[];
  user: IUserV0;
  treeView: boolean;
  loggedUser: ILoggedUser;
  handleGoToUrl: (msg: string) => void;
  //handleNotification: (msg: string | null | undefined) => void;
}

const MyPurchasesGrid: React.FC<IMyPurchasesGridProps> = ({
  orders,
  user,
  loggedUser,
  handleGoToUrl,
}) => {
  const { styles, props } = useDataGridStyles({
    variant: "common",
    type: "purchases",
  });

  // Language sync is handled globally in App.tsx
  const res = useResources(myPurchasesResources);

  const columnsCustomer: GridColDef[] = [
    { field: "orderId", headerName: res.orderId, width: 150 },
    {
      field: "date",
      headerName: res.date,
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        return (
          <div>
            {!row.parentId &&
              (row.date ? format(new Date(row.date), "dd/MM/yyyy") : "")}
          </div>
        );
      },
    },
    {
      field: "singleProductOrder",
      headerName: res.product,
      width: 300,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        const program = row.details[row.index];
        const url = urls.getMyDownloads(
          row?.endUserId || "",
          program.programId
        );
        return (
          <div>
            {!row.hasChildren && (
              <Link
                key={url}
                href="#"
                color={program.isCancelled === true ? "secondary" : "primary"}
                onClick={(e) => {
                  e.preventDefault();
                  handleGoToUrl(url);
                }}
              >
                {loggedUser.isCustomer
                  ? program.productName
                  : program.programName}
              </Link>
            )}
          </div>
        );
      },
    },
  ];

  const columnsDist: GridColDef[] = [
    { field: "orderId", headerName: res.orderId, width: 150 },
    {
      field: "date",
      headerName: res.date,
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        return (
          <div>
            {!row.parentId &&
              (row.date ? format(new Date(row.date), "dd/MM/yyyy") : "")}
          </div>
        );
      },
    },
    { field: "endUserName", headerName: res.customerName, width: 250 },
    { field: "endUserCompany", headerName: res.companyName, width: 300 },
    { field: "ownerName", headerName: res.representedBy, width: 250 },
    {
      field: "singleProductOrder",
      headerName: res.product,
      width: 340,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        const program = row.details[row.index];
        const url = urls.getMyDownloads(
          row?.endUserId || "",
          program.programId
        );
        return (
          <div>
            {!row.hasChildren && (
              <Link
                key={url}
                href="#"
                color={program.isCancelled === true ? "secondary" : "primary"}
                onClick={(e) => {
                  e.preventDefault();
                  handleGoToUrl(url);
                }}
              >
                {loggedUser.isCustomer
                  ? program.productName
                  : program.programName}
              </Link>
            )}
          </div>
        );
      },
    },
    {
      field: "licenses",
      headerName: "",
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        const program = row.details[row.index];
        const url = urls.getProjectLicensesUrl(
          program.programId,
          program.productId,
          row.endUserId,
          program.productName
        );
        return (
          <div>
            {!row.hasChildren && (
              <Link
                key={url}
                href={url}
                onClick={(e) => {
                  e.preventDefault();
                  handleGoToUrl(url);
                }}
                color={program.isCancelled === true ? "secondary" : "primary"}
              >
                {res.licenses}
              </Link>
            )}
          </div>
        );
      },
    },
  ];

  const renderColumns = () => {
    if (user?.isCustomer) {
      return columnsCustomer;
    }
    return columnsDist;
  };

  return (
    <div style={{ height: 600, width: "100%" }}>
      <DataGrid
        rows={orders}
        columns={renderColumns()}
        getRowId={(row) => row.id || row.orderId || Math.random()}
        sx={styles}
        {...props}
        slots={{ toolbar: GridToolbar }}
        initialState={{
          ...props.initialState,
          columns: {
            columnVisibilityModel: {
              endUserName: user?.isCustomer === true,
              ownerName: user?.isCustomer === true,
            },
          },
        }}
      />
    </div>
  );
};

export default MyPurchasesGrid;
